import React, { useState } from "react";
import { X } from "lucide-react";
import { api } from "@/platform/api";
import { Button, ACCENT } from "@/platform/components/primitives";

// Shown once, the first time the dashboard opens (when the user has no
// referralSource yet). Lightweight attribution survey \u2014 "How did you find us?".
const OPTIONS = [
  "Google search",
  "Recommended by a friend or colleague",
  "Social media (LinkedIn, X, etc.)",
  "A blog or article",
  "YouTube",
  "Other",
];

export default function ReferralSurveyModal({ onDone }) {
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const finish = async (source, extra) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/api/auth/survey/referral", { source, detail: extra || "" });
    } catch (_e) {
      /* non-blocking \u2014 never trap the user in the modal */
    }
    onDone?.(source);
  };

  const submit = () => {
    if (!selected) return;
    finish(selected, selected === "Other" ? detail : "");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-5" data-testid="referral-survey">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => finish("skipped")} />
      <div className="relative w-full max-w-[440px] animate-in fade-in zoom-in-95 duration-300 rounded-2xl border border-[#242A38] bg-[#151A24] p-7 shadow-2xl">
        <button
          onClick={() => finish("skipped")}
          className="absolute right-4 top-4 text-[#6E7686] hover:text-[#AAB1BF]"
          aria-label="Skip"
          data-testid="survey-skip"
        >
          <X className="h-5 w-5" />
        </button>

        <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#A99BFF" }}>
          One quick thing
        </span>
        <h2 className="mt-2 text-[22px] font-semibold tracking-tight text-[#F4F5F8]">How did you find us?</h2>
        <p className="mt-1.5 text-[14px] text-[#98A0B0]">This helps us reach more teams like yours.</p>

        <div className="mt-5 space-y-2">
          {OPTIONS.map((o) => {
            const active = selected === o;
            return (
              <button
                key={o}
                onClick={() => setSelected(o)}
                data-testid={`survey-option-${o.split(" ")[0].toLowerCase()}`}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-[14px] transition-colors ${
                  active ? "border-[#6D5EF6] bg-[#0E1016]" : "border-[#2A3040] hover:border-[#39415A]"
                }`}
              >
                <span
                  className="grid h-4 w-4 place-items-center rounded-full border"
                  style={{ borderColor: active ? "#171717" : "#4A5164" }}
                >
                  {active && <span className="h-2 w-2 rounded-full" style={{ background: ACCENT }} />}
                </span>
                <span className="text-[#C4C9D4]">{o}</span>
              </button>
            );
          })}
        </div>

        {selected === "Other" && (
          <input
            autoFocus
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Tell us more (optional)"
            className="mt-3 w-full rounded-lg border border-[#2A3040] bg-[#151A24] px-3.5 py-2.5 text-[14px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]"
            data-testid="survey-other-input"
          />
        )}

        <div className="mt-6">
          <Button onClick={submit} disabled={!selected} loading={submitting} data-testid="survey-submit">
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
